import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة تحويل أحجام قوالب الكيك - تعديل كميات الوصفة";
const DESCRIPTION =
  "حاسبة مجانية لتحويل كميات الكيك بين قوالب مختلفة. أدخل حجم القالب الأصلي والجديد واحصل على نسبة التعديل لجميع المكونات بدقة.";
const KEYWORDS =
  "حاسبة قوالب الكيك, تحويل قوالب الكيك, تعديل وصفة الكيك, حجم قالب الكيك, حاسبة الخبيز, cake pan conversion calculator, تحويل حجم الكيك, قالب دائري مستطيل";
const OG_URL = "https://do-calculate.com/calculator/ar/cake-pan-conversion/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة تحويل أحجام قوالب الكيك",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة تحويل أحجام قوالب الكيك",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "كيف أحوّل وصفة كيك من قالب دائري إلى مستطيل؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "احسب مساحة القالب الأصلي والجديد، ثم اقسم مساحة القالب الجديد على الأصلي للحصول على نسبة التعديل. اضرب كل مكون في الوصفة بهذه النسبة.",
        },
      },
      {
        "@type": "Question",
        name: "ماذا يحدث إذا استخدمت قالباً أكبر دون تعديل الكميات؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "سيكون الكيك أرق وربما يجف أسرع، لأن العجينة ستنتشر على مساحة أكبر. يجب دائماً تعديل الكميات أو وقت الخبز عند تغيير حجم القالب.",
        },
      },
      {
        "@type": "Question",
        name: "هل تتغير درجة الحرارة عند تغيير حجم القالب؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "درجة الحرارة تبقى عادةً نفسها، لكن وقت الخبز يتغير. القوالب الأكبر أو الأعمق تحتاج وقتاً أطول، والأصغر تحتاج وقتاً أقصر. تحقق من النضج بعود الأسنان.",
        },
      },
    ],
  },
];

// Pan shapes
const PAN_SHAPES = {
  round: "دائري",
  square: "مربع",
  rectangular: "مستطيل",
  bundt: "بوندت / حلقي",
  springform: "سبرينجفورم دائري",
};

// Calculate pan area in cm²
const calcPanArea = (shape, d1, d2, depth) => {
  const h = parseFloat(depth) || 0;
  switch (shape) {
    case "round":
    case "springform": {
      const r = parseFloat(d1) / 2;
      return Math.PI * r * r * h;
    }
    case "square": {
      const s = parseFloat(d1);
      return s * s * h;
    }
    case "rectangular": {
      return parseFloat(d1) * parseFloat(d2) * h;
    }
    case "bundt": {
      // approximate: outer diameter minus a center tube hole ~1/3 outer diameter
      const R = parseFloat(d1) / 2;
      const r = R * 0.35;
      return Math.PI * (R * R - r * r) * h;
    }
    default:
      return 0;
  }
};

function CakePanCalculator() {
  // Original pan
  const [origShape, setOrigShape] = useState("round");
  const [origD1, setOrigD1] = useState("");
  const [origD2, setOrigD2] = useState("");
  const [origDepth, setOrigDepth] = useState("");

  // New pan
  const [newShape, setNewShape] = useState("square");
  const [newD1, setNewD1] = useState("");
  const [newD2, setNewD2] = useState("");
  const [newDepth, setNewDepth] = useState("");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const needsD2 = (shape) => shape === "rectangular";

  const getD1Label = (shape) => {
    if (shape === "round" || shape === "springform" || shape === "bundt") return "القطر (cm)";
    if (shape === "square") return "طول الضلع (cm)";
    return "الطول (cm)";
  };

  const calculateResult = () => {
    try {
      setError("");

      const oD1 = parseFloat(origD1);
      const oD2 = needsD2(origShape) ? parseFloat(origD2) : 0;
      const oDepth = parseFloat(origDepth);
      const nD1 = parseFloat(newD1);
      const nD2 = needsD2(newShape) ? parseFloat(newD2) : 0;
      const nDepth = parseFloat(newDepth);

      if (!oD1 || !oDepth || !nD1 || !nDepth) {
        setResult(null);
        return;
      }
      if (needsD2(origShape) && !oD2) {
        setResult(null);
        return;
      }
      if (needsD2(newShape) && !nD2) {
        setResult(null);
        return;
      }

      if (oD1 <= 0 || nD1 <= 0 || oDepth <= 0 || nDepth <= 0) {
        setError("يرجى إدخال قيم موجبة وصحيحة");
        setResult(null);
        return;
      }

      const origVolume = calcPanArea(origShape, oD1, oD2, oDepth);
      const newVolume = calcPanArea(newShape, nD1, nD2, nDepth);

      if (origVolume <= 0 || newVolume <= 0) {
        setError("تعذّر حساب الحجم. تحقق من القيم المدخلة.");
        setResult(null);
        return;
      }

      const ratio = newVolume / origVolume;

      // Baking time adjustment note
      let timeNote = "";
      if (ratio > 1.5) {
        timeNote = "الوصفة الجديدة أكبر بكثير — أضف 10-20 دقيقة لوقت الخبز وراقب الكيك";
      } else if (ratio > 1.1) {
        timeNote = "القالب الجديد أكبر قليلاً — أضف 5-10 دقائق لوقت الخبز";
      } else if (ratio < 0.7) {
        timeNote = "القالب الجديد أصغر بكثير — قلّل وقت الخبز بحوالي 10-15 دقيقة";
      } else if (ratio < 0.9) {
        timeNote = "القالب الجديد أصغر قليلاً — قلّل وقت الخبز بحوالي 5 دقائق";
      } else {
        timeNote = "حجم القالب متقارب جداً — يمكن الإبقاء على نفس وقت الخبز تقريباً";
      }

      setResult({
        ratio,
        origVolume,
        newVolume,
        origShapeName: PAN_SHAPES[origShape],
        newShapeName: PAN_SHAPES[newShape],
        timeNote,
        origDesc: origShape === "rectangular"
          ? `${oD1} × ${oD2} cm، عمق ${oDepth} cm`
          : `${oD1} cm، عمق ${oDepth} cm`,
        newDesc: newShape === "rectangular"
          ? `${nD1} × ${nD2} cm، عمق ${nDepth} cm`
          : `${nD1} cm، عمق ${nDepth} cm`,
      });
    } catch (err) {
      setError("خطأ في الحساب: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    if (origD1 && origDepth && newD1 && newDepth) {
      calculateResult();
    } else {
      setResult(null);
      setError("");
    }
  }, [origShape, origD1, origD2, origDepth, newShape, newD1, newD2, newDepth]);

  const handleClear = () => {
    setOrigShape("round");
    setOrigD1("");
    setOrigD2("");
    setOrigDepth("");
    setNewShape("square");
    setNewD1("");
    setNewD2("");
    setNewDepth("");
    setResult(null);
    setError("");
  };

  const renderOrigInputs = () => (
    <>
      <div className="row">
        <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
          <label >{getD1Label(origShape)}</label>
          <input
            type="number"
            className="form-control"
            value={origD1}
            onChange={(e) => setOrigD1(e.target.value)}
            min="0"
            step="0.5"
            placeholder={getD1Label(origShape)}
          />
        </div>
        {needsD2(origShape) && (
          <div className="col-sm-12 col-md-4">
            <label >العرض (cm)</label>
            <input
              type="number"
              className="form-control"
              value={origD2}
              onChange={(e) => setOrigD2(e.target.value)}
              min="0"
              step="0.5"
              placeholder="العرض"
            />
          </div>
        )}
        <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
          <label >العمق / الارتفاع (cm)</label>
          <input
            type="number"
            className="form-control"
            value={origDepth}
            onChange={(e) => setOrigDepth(e.target.value)}
            min="0"
            step="0.5"
            placeholder="العمق"
          />
        </div>
      </div>
    </>
  );

  const renderNewInputs = () => (
    <>
      <div className="row">
        <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
          <label >{getD1Label(newShape)}</label>
          <input
            type="number"
            className="form-control"
            value={newD1}
            onChange={(e) => setNewD1(e.target.value)}
            min="0"
            step="0.5"
            placeholder={getD1Label(newShape)}
          />
        </div>
        {needsD2(newShape) && (
          <div className="col-sm-12 col-md-4">
            <label >العرض (cm)</label>
            <input
              type="number"
              className="form-control"
              value={newD2}
              onChange={(e) => setNewD2(e.target.value)}
              min="0"
              step="0.5"
              placeholder="العرض"
            />
          </div>
        )}
        <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
          <label >العمق / الارتفاع (cm)</label>
          <input
            type="number"
            className="form-control"
            value={newDepth}
            onChange={(e) => setNewDepth(e.target.value)}
            min="0"
            step="0.5"
            placeholder="العمق"
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة تحويل أحجام قوالب الكيك</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة تحويل قوالب الكيك</strong> تحل مشكلة كل شخص يحب الخبيز: ماذا أفعل لو وصفتي مكتوبة لقالب 20 cm دائري لكن عندي فقط قالب مستطيل؟ أدخل أبعاد قالبك الأصلي والجديد، وستحصل فوراً على <strong>نسبة تعديل المكونات</strong> بدقة. لا تخمين، لا كيكة فاشلة.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  حوّل وصفتك بين القوالب بسهولة
                </h2>
              </div>

              {/* Original Pan */}
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#1a5f3f" }}>
                  🎂 القالب الأصلي (قالب الوصفة)
                </h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label >شكل القالب</label>
                    <select
                      className="form-select"
                      value={origShape}
                      onChange={(e) => {
                        setOrigShape(e.target.value);
                        setOrigD2("");
                      }}
                    >
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (
                        <option key={key} value={key}>{val}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {renderOrigInputs()}
              </div>

              {/* New Pan */}
              <div
                style={{
                  backgroundColor: "#fff8f0",
                  border: "1px solid #ffc98b",
                  borderRadius: "8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#8a4500" }}>
                  🍰 القالب الجديد (الذي ستستخدمه)
                </h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label >شكل القالب</label>
                    <select
                      className="form-select"
                      value={newShape}
                      onChange={(e) => {
                        setNewShape(e.target.value);
                        setNewD2("");
                      }}
                    >
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (
                        <option key={key} value={key}>{val}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {renderNewInputs()}
              </div>

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  امسح
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>النتيجة:</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            البيان
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            القيمة
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>نسبة تعديل الكميات</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "22px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            × {result.ratio.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>حجم القالب الأصلي</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {result.origVolume.toFixed(1)} cm³
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>حجم القالب الجديد</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {result.newVolume.toFixed(1)} cm³
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الفرق في الحجم</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "16px",
                              color: result.ratio > 1 ? "#0f3d28" : "#8a0000",
                            }}
                          >
                            {result.ratio > 1
                              ? `+${((result.ratio - 1) * 100).toFixed(0)}%`
                              : `${((result.ratio - 1) * 100).toFixed(0)}%`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>كيف تطبّق النسبة؟</strong>
                      </div>
                      <div className="info-section-body">
                        <p style={{ lineHeight: "1.8" }}>
                          اضرب كل مكوّن في وصفتك الأصلية بالعامل{" "}
                          <strong
                            style={{ fontSize: "18px", color: "#1a5f3f" }}
                          >
                            {result.ratio.toFixed(2)}
                          </strong>
                        </p>
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2.2" }}>
                          <li>🥚 <strong>مثال — 3 بيضات:</strong> {(3 * result.ratio).toFixed(1)} بيضة</li>
                          <li>🧈 <strong>مثال — 200 جم زبدة:</strong> {(200 * result.ratio).toFixed(0)} جم</li>
                          <li>🍚 <strong>مثال — 250 جم دقيق:</strong> {(250 * result.ratio).toFixed(0)} جم</li>
                          <li>🍬 <strong>مثال — 150 جم سكر:</strong> {(150 * result.ratio).toFixed(0)} جم</li>
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 ملاحظة:</strong> للبيض، إذا كانت النتيجة كسراً مثل 2.4، استخدم 2 بيضات وأضف جزءاً من بيضة ثالثة مخفوقة.
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب ونصيحة الخبز</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>القالب الأصلي:</strong> {result.origShapeName} — {result.origDesc}
                          </p>
                          <p>
                            <strong>القالب الجديد:</strong> {result.newShapeName} — {result.newDesc}
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}>
                            <strong>الحساب:</strong> نسبة التعديل = حجم القالب الجديد ÷ حجم القالب الأصلي
                            <br />
                            = {result.newVolume.toFixed(1)} ÷ {result.origVolume.toFixed(1)} = <strong>{result.ratio.toFixed(3)}</strong>
                          </p>
                          <hr />
                          <div className="alert alert-warning mt-2" style={{ fontSize: "14px" }}>
                            <strong>⏱️ وقت الخبز:</strong> {result.timeNote}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة تحويل أحجام قوالب الكيك؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  هي أداة ذكية تحسب لك نسبة تعديل مكونات الكيك تلقائياً عند تغيير حجم القالب أو شكله. الفكرة بسيطة: كل قالب له حجم (مساحة × عمق)، وعندما تنتقل بين قالبين مختلفين، تتغير كميات العجينة المطلوبة بنفس النسبة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام الحاسبة:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر شكل القالب الأصلي (الموجود في الوصفة) وأدخل أبعاده</li>
                  <li>اختر شكل القالب الجديد الذي ستستخدمه وأدخل أبعاده</li>
                  <li>ستظهر النتيجة فوراً: عامل الضرب الذي تطبّقه على كل مكون</li>
                  <li>اقرأ نصيحة وقت الخبز في نهاية النتائج</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 ملاحظة:</strong> الحاسبة تعمل مع القوالب الدائرية، المربعة، المستطيلة، البوندت، والسبرينجفورم. أدخل الأبعاد بالسنتيمتر للحصول على أدق النتائج.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات حاسبة تحويل قوالب الكيك</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  في المطبخ المنزلي:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>تكبير وصفة لعدد ضيوف أكثر:</strong> حوّل وصفة قالب 20 cm إلى قالب 26 cm لتغطية احتياجات العائلة
                  </li>
                  <li>
                    <strong>تصغير الوصفة:</strong> تريد كيكة صغيرة لشخصين؟ حوّل الكميات لقالب أصغر
                  </li>
                  <li>
                    <strong>استخدام ما لديك:</strong> ليس عندك القالب المطلوب؟ أدخل ما لديك واحصل على الكميات الصحيحة
                  </li>
                  <li>
                    <strong>تجربة أشكال جديدة:</strong> جرّب تحويل وصفة دائرية إلى قالب بوندت للتنويع
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  للمخابز والحلويات الاحترافية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>التوحيد القياسي:</strong> إنتاج كيكات بأحجام محددة باستمرار
                  </li>
                  <li>
                    <strong>حسابات التكلفة:</strong> معرفة كمية المكونات بدقة يساعد في تسعير المنتجات
                  </li>
                  <li>
                    <strong>تنفيذ الطلبات المخصصة:</strong> زبون يريد كيكة بحجم غير معتاد؟ احسب الكميات بثقة
                  </li>
                  <li>
                    <strong>تدريب الموظفين:</strong> أداة مرجعية سريعة للطهاة الجدد
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  لمحبي الخبيز والتعلم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>فهم العلاقة بين الحجم والكميات:</strong> تعلّم الرياضيات بطريقة ممتعة
                  </li>
                  <li>
                    <strong>تجنب الإهدار:</strong> لا مزيد من كيكات فائضة عن القالب أو شحيحة
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>مساحات القوالب الشائعة ومقارنتها</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  معرفة مساحة القالب هي أساس الحساب. يمكنك استخدام الجدول أدناه كمرجع سريع للقوالب الأكثر استخداماً (بعمق 5 cm):
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>نوع القالب</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>الأبعاد</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>الحجم التقريبي (cm³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>دائري</td>
                      <td style={{ textAlign: "center" }}>20 cm قطر</td>
                      <td style={{ textAlign: "center" }}>1,571</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>دائري</td>
                      <td style={{ textAlign: "center" }}>23 cm قطر</td>
                      <td style={{ textAlign: "center" }}>2,083</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>دائري</td>
                      <td style={{ textAlign: "center" }}>26 cm قطر</td>
                      <td style={{ textAlign: "center" }}>2,659</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>مربع</td>
                      <td style={{ textAlign: "center" }}>20 × 20 cm</td>
                      <td style={{ textAlign: "center" }}>2,000</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>مستطيل</td>
                      <td style={{ textAlign: "center" }}>30 × 20 cm</td>
                      <td style={{ textAlign: "center" }}>3,000</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>مستطيل</td>
                      <td style={{ textAlign: "center" }}>33 × 23 cm</td>
                      <td style={{ textAlign: "center" }}>3,795</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيف تحسب المساحة يدوياً؟
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>دائري:</strong> π × نصف القطر² × العمق</li>
                  <li><strong>مربع:</strong> الضلع² × العمق</li>
                  <li><strong>مستطيل:</strong> الطول × العرض × العمق</li>
                  <li><strong>بوندت:</strong> π × (نصف القطر الخارجي² − نصف القطر الداخلي²) × العمق</li>
                </ul>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ فائدة:</strong> قالب مربع 20 cm يساوي تقريباً قالب دائري 23 cm من حيث الحجم — معلومة تُنقذك كثيراً في المطبخ.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على تحويل القوالب</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: من دائري 20 cm إلى دائري 25 cm
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الوضع:</strong> وصفتك لقالب دائري قطره 20 cm وعمقه 5 cm، وتريد استخدام قالب 25 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب:</strong><br />
                  حجم القالب الأصلي = π × 10² × 5 = 1,571 cm³<br />
                  حجم القالب الجديد = π × 12.5² × 5 = 2,454 cm³<br />
                  نسبة التعديل = 2,454 ÷ 1,571 = <strong>1.56</strong><br />
                  اضرب جميع المكونات × 1.56
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 2: من مستطيل إلى دائري
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الوضع:</strong> وصفتك لقالب مستطيل 33 × 23 cm بعمق 5 cm، وتريد تحويلها لقالب دائري 26 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب:</strong><br />
                  حجم المستطيل = 33 × 23 × 5 = 3,795 cm³<br />
                  حجم الدائري = π × 13² × 5 = 2,659 cm³<br />
                  نسبة التعديل = 2,659 ÷ 3,795 = <strong>0.70</strong><br />
                  استخدم 70% فقط من كميات الوصفة
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 3: تكبير الوصفة للمناسبات
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الوضع:</strong> وصفة لقالب مربع 20 × 20 cm بعمق 5 cm، وتريد قالباً مستطيلاً 40 × 30 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>الحساب:</strong><br />
                  حجم المربع = 20 × 20 × 5 = 2,000 cm³<br />
                  حجم المستطيل = 40 × 30 × 5 = 6,000 cm³<br />
                  نسبة التعديل = 6,000 ÷ 2,000 = <strong>3.0</strong><br />
                  ثلاثة أضعاف جميع المكونات
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 4: تصغير وصفة لكيكة فردية
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>الوضع:</strong> وصفة لقالب دائري 24 cm بعمق 6 cm، وتريد قالباً صغيراً 15 cm بعمق 5 cm.
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>النتيجة:</strong> نسبة التعديل ≈ 0.41 — أي نحو 40% فقط من الوصفة الأصلية. وقلّص وقت الخبز بنحو 10-15 دقيقة.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>نصائح لتحويل القوالب بنجاح</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  نصائح أساسية:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>قِس أبعاد قالبك من الداخل:</strong> المقاسات المكتوبة على القالب تكون من الحافة الخارجية أحياناً
                  </li>
                  <li>
                    <strong>راقب الكيكة مبكراً:</strong> إذا كبّرت القالب، ابدأ تفحص النضج قبل الوقت المحدد بـ10 دقائق
                  </li>
                  <li>
                    <strong>لا تملأ القالب أكثر من ثلثيه:</strong> العجينة ترتفع أثناء الخبز
                  </li>
                  <li>
                    <strong>درجة الحرارة لا تتغير:</strong> فقط وقت الخبز يتأثر بتغيير الحجم
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  لتجنب الأخطاء:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>أدخل جميع القياسات بنفس الوحدة (cm)</li>
                  <li>لا تنسَ إدخال العمق — هو جزء أساسي من الحساب</li>
                  <li>إذا كان عامل الضرب أكبر من 2، فكّر في خبز الوصفة على دفعتين</li>
                  <li>للبيض، قرّب للأعداد الصحيحة الأقرب أو استخدم جزءاً من بيضة مخفوقة</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  عند تغيير شكل القالب:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الانتقال من دائري لمربع:</strong> الزوايا تُنضج أسرع — راقبها جيداً
                  </li>
                  <li>
                    <strong>قالب البوندت:</strong> يحتاج تشحيماً جيداً جداً لأن فك الكيكة أصعب
                  </li>
                  <li>
                    <strong>القوالب الداكنة:</strong> تمتص الحرارة أكثر — قلّل درجة الحرارة 10-15 درجة
                  </li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>⚠️ تنبيه:</strong> تعديل الكميات لا يعني بالضرورة أن طعم الكيكة سيكون مثالياً في كل مرة. بعض الوصفات — كالمافن والسوفليه — حساسة للتعديل وتحتاج تجربة قبل المناسبات المهمة.
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>الأسئلة الشائعة عن تحويل قوالب الكيك</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  هل تتغير درجة حرارة الفرن عند تغيير القالب؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  لا في الغالب. درجة الحرارة تبقى كما هي في الوصفة الأصلية. الذي يتغير هو <strong>وقت الخبز</strong>: القوالب الأكبر أو الأعمق تحتاج وقتاً أطول، والأصغر وقتاً أقصر. الاستثناء هو القوالب الداكنة اللون، فعليك تخفيض الحرارة 10-15 درجة لأنها تمتص الحرارة أسرع.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  ماذا لو استخدمت قالباً أعمق بدون تغيير العرض؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  عمق القالب جزء من حسابنا، وتغييره يغيّر الحجم الإجمالي. قالب 20 cm بعمق 5 cm يختلف عن نفس القطر بعمق 8 cm بنسبة 60%. إذا أبقيت نفس الكمية في قالب أعمق، سيكون الكيك أقصر وقد لا يصل للنضج بالتساوي.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  هل يمكن تحويل وصفة كيك طبقات (layer cake) لقالب واحد؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  نعم، احسب الحجم الإجمالي لجميع الطبقات معاً وقارنه بحجم القالب الواحد. مثلاً: طبقتان بقالب 20 cm وعمق 5 cm تساويان حجماً إجمالياً 3,142 cm³، إذا حوّلتها لقالب واحد 20 cm بعمق 10 cm فالنسبة 1:1. لكن تذكّر ألا تملأ القالب أكثر من ثلثيه.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  كيف أتعامل مع مكوّن كالبيكنج باودر عند التعديل؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  لا تضرب البيكنج باودر وغيره من مواد الرفع بنفس نسبة التعديل دائماً. القاعدة العامة: ابدأ بتطبيق النسبة كاملاً، لكن إذا كانت النسبة أكبر من 2، خفّض مقدار البيكنج باودر قليلاً (بنسبة 80% بدلاً من 100%) لتجنب طعم التلوين المعدني أو انهيار الكيكة.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  هل حاسبة القوالب مفيدة للبراونيز والباي؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  نعم، تعمل بشكل ممتاز مع البراونيز والباي والتارت. المبدأ نفسه: الحجم هو أساس الحساب. الفرق الوحيد أن بعض وصفات الباي تحتوي حشوة وعجينة منفصلة، لذا احسب نسبة التعديل وطبّقها على كل جزء بشكل مستقل.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  هل يمكن استخدام قالب السيليكون بنفس الطريقة؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  بالتأكيد. حساب الحجم نفسه ينطبق على كل المواد. لكن السيليكون يوزّع الحرارة بشكل مختلف عن المعدن، لذا قد تحتاج إضافة 5 دقائق لوقت الخبز وتأكّد من النضج بعود الأسنان.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  ماذا أفعل إذا كانت نسبة التعديل أقل من 0.5؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  نسبة أقل من 0.5 تعني أن قالبك الجديد أصغر بأكثر من النصف. في هذه الحالة، بعض مكونات الوصفة ستكون بكميات صغيرة جداً يصعب قياسها بدقة. الحل: اخبز نصف الوصفة الأصلية واستخدم الباقي في خبزة ثانية أو ثلجّه.
                </p>
                <hr />
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  لماذا نعتمد على الحجم وليس المساحة فقط في الحساب؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  لأن الكيك ثلاثي الأبعاد. قالبان بنفس المساحة السطحية لكن بعمق مختلف يحتاجان كميات مختلفة تماماً. مثلاً: قالبان قطرهما 20 cm، الأول عمقه 5 cm والثاني 8 cm — الأول يحتاج 37.5% أقل من العجينة. لذا يجب دائماً إدخال العمق في الحسابات.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CakePanCalculator;